import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../../api';
import { useAuth } from '../../contexts/AuthContexte';
import { useAccess } from '../../contexts/AccessContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useLanguage, getLocale } from '../../contexts/LanguageContext';
import PageHeader from '../ui/PageHeader';
import Icon from '../ui/Icon';
import './EditSaisonPage.css';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(getLocale(), { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const fmtNum = (n) => (n || 0).toLocaleString(getLocale()).replace(/ /g, ' ');

const EMPTY_FORM = {
    title: '',
    startDate: '',
    endDate: '',
    description: '',
    publicationChannel: '',
    attackGrade: '',
    defenseGrade: '',
    autoPublish: false,
    includeTempo: true,
    channelConfigId: ''
};

const seasonStatus = (saison) => {
    if (saison.archived) return { key: 'archived', label: 'Archivée', tone: '' };
    const now = Date.now();
    if (new Date(saison.startDate).getTime() > now) return { key: 'upcoming', label: 'À venir', tone: 'purple' };
    if (new Date(saison.endDate).getTime() < now) return { key: 'ended', label: 'Terminée', tone: '' };
    return { key: 'running', label: 'En cours', tone: 'green' };
};

const EditSaisonPage = () => {
    const { serverId, saisonId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { can } = useAccess();
    const { success, error: showError, warning, info } = useNotification();
    const { t } = useLanguage();
    const auteur = user?.username || 'Inconnu';

    // Saison et formulaire de configuration
    const [saison, setSaison] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [loading, setLoading] = useState(true);
    const [configOpen, setConfigOpen] = useState(false);
    const [savingConfig, setSavingConfig] = useState(false);

    // Listes de référence
    const [salons, setSalons] = useState([]);
    const [attackBaremes, setAttackBaremes] = useState([]);
    const [defenseBaremes, setDefenseBaremes] = useState([]);
    const [channelConfigs, setChannelConfigs] = useState([]);

    // Classement
    const [classement, setClassement] = useState([]);
    const [modifications, setModifications] = useState({});   // { joueurId: ajustement }
    const [adjustDrafts, setAdjustDrafts] = useState({});     // { joueurId: texte en cours de saisie ("-", "", "-15"...) }
    const [usernames, setUsernames] = useState({});
    const [namesLoading, setNamesLoading] = useState(false);
    const [rankingLoading, setRankingLoading] = useState(true);
    const [savingRanking, setSavingRanking] = useState(false);
    const [search, setSearch] = useState('');

    // Publication
    const [publishOpen, setPublishOpen] = useState(false);
    const [publishChannel, setPublishChannel] = useState('');
    const [channelSearch, setChannelSearch] = useState('');
    const [publishing, setPublishing] = useState(false);

    const setField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    // ── Chargement de la saison ──────────────────────────────────────
    useEffect(() => {
        if (!saisonId) return;
        setLoading(true);
        api.get(`/saison/${saisonId}`)
            .then(res => {
                const s = res.data;
                setSaison(s);
                setForm({
                    title: s.title || '',
                    startDate: s.startDate?.substring(0, 10) || '',
                    endDate: s.endDate?.substring(0, 10) || '',
                    description: s.description || '',
                    publicationChannel: s.publicationChannel || '',
                    attackGrade: s.attackGrade || '',
                    defenseGrade: s.defenseGrade || '',
                    autoPublish: s.autoPublish || false,
                    includeTempo: s.includeTempo ?? true,
                    channelConfigId: s.channelConfigId || ''
                });
            })
            .catch(() => {
                showError(t('editSeason.notFound', 'Saison introuvable'));
                navigate(`/server/${serverId}/saison`);
            })
            .finally(() => setLoading(false));
    }, [saisonId, serverId, navigate, showError, t]);

    // ── Salons, barèmes, configurations de salons ───────────────────
    useEffect(() => {
        if (!serverId) return;
        api.get(`/discord/channels/${serverId}`)
            .then(res => setSalons((res.data || []).filter(c => c.type === 0).sort((a, b) => a.name.localeCompare(b.name))))
            .catch(() =>
                api.get(`/servers/${serverId}`)
                    .then(res => setSalons((res.data?.salons || []).filter(c => c.type === 0).sort((a, b) => a.name.localeCompare(b.name))))
                    .catch(() => setSalons([]))
            );
        api.get(`/baremes/${serverId}`)
            .then(res => {
                const baremes = res.data || [];
                setAttackBaremes(baremes.filter(b => b.type === 'attaque'));
                setDefenseBaremes(baremes.filter(b => b.type === 'défense'));
            })
            .catch(err => console.error('Erreur barèmes :', err));
        api.get(`/channel-configs/${serverId}`)
            .then(res => setChannelConfigs(res.data || []))
            .catch(() => setChannelConfigs([]));
    }, [serverId]);

    // ── Pseudos : modèle Joueur d'abord, puis Discord pour les manquants ──
    const loadNames = useCallback(async (ids, forceRefresh = false) => {
        const wanted = [...new Set(ids.filter(Boolean))];
        if (wanted.length === 0) return 0;
        setNamesLoading(true);
        let found = {};
        let remaining = wanted;
        try {
            const res = await api.post('/players/display-names', { playerIds: wanted, serverId });
            if (res.data?.success) {
                found = { ...res.data.displayNames };
                remaining = res.data.notFound || [];
            }
        } catch (err) {
            console.warn('Pseudos (modèle Joueur) indisponibles :', err.message);
        }
        if (remaining.length > 0) {
            try {
                const res = await api.post('/discord/users', { userIds: remaining, serverId, forceRefresh });
                found = { ...found, ...(res.data || {}) };
            } catch (err) {
                console.warn('Pseudos (Discord) indisponibles :', err.message);
            }
        }
        setUsernames(prev => ({ ...prev, ...found }));
        setNamesLoading(false);
        return Object.keys(found).length;
    }, [serverId]);

    // ── Classement ──────────────────────────────────────────────────
    const loadClassement = useCallback(async () => {
        setRankingLoading(true);
        try {
            const res = await api.get(`/saison/${saisonId}/classement`);
            const rows = (Array.isArray(res.data) ? res.data : []).filter(j => j?.joueurId);
            setClassement(rows);
            setModifications(Object.fromEntries(rows.map(j => [j.joueurId, j.pointsModifies || 0])));
            setRankingLoading(false);
            await loadNames(rows.map(j => j.joueurId));
            return true;
        } catch (err) {
            console.error('Erreur classement :', err);
            setRankingLoading(false);
            return false;
        }
    }, [saisonId, loadNames]);

    useEffect(() => { if (saisonId) loadClassement(); }, [saisonId, loadClassement]);

    const changedMods = classement.filter(j => (modifications[j.joueurId] ?? 0) !== (j.pointsModifies || 0));
    const rankingDirty = changedMods.length > 0;

    // Champ texte : accepte le signe « - » et une saisie vide le temps de taper
    const setModification = (joueurId, value) => {
        if (!/^-?\d*$/.test(value)) return;
        setAdjustDrafts(prev => ({ ...prev, [joueurId]: value }));
        const parsed = parseInt(value, 10);
        setModifications(prev => ({ ...prev, [joueurId]: Number.isNaN(parsed) ? 0 : parsed }));
    };

    const endAdjustEdit = (joueurId) =>
        setAdjustDrafts(prev => { const next = { ...prev }; delete next[joueurId]; return next; });

    const rows = useMemo(() => classement.map((j, index) => {
        const modif = modifications[j.joueurId] ?? 0;
        return { ...j, rank: index + 1, modif, total: (j.points || 0) + modif, name: usernames[j.joueurId] || null };
    }), [classement, modifications, usernames]);

    const query = search.trim().toLowerCase();
    const visibleRows = query ? rows.filter(r => (r.name || r.joueurId).toLowerCase().includes(query)) : rows;

    // ── Actions ─────────────────────────────────────────────────────
    const saveConfig = async (e) => {
        e.preventDefault();
        setSavingConfig(true);
        try {
            await api.put(`/saison/${saisonId}`, { ...form, modifiedBy: auteur });
            setSaison(prev => ({ ...prev, title: form.title, startDate: form.startDate, endDate: form.endDate }));
            success(t('editSeason.configSaved', 'Configuration enregistrée'));
            // Barèmes, dates ou salons ont pu changer : le classement est recalculé
            await loadClassement();
        } catch (err) {
            showError(err?.response?.data?.error || t('editSeason.configSaveError', 'Erreur lors de l\'enregistrement de la configuration'));
        } finally {
            setSavingConfig(false);
        }
    };

    const saveRanking = async () => {
        setSavingRanking(true);
        try {
            let failures = 0;
            for (const j of changedMods) {
                try {
                    await api.post(`/saison/${saisonId}/player/${j.joueurId}/modify-points`, {
                        pointsModifies: modifications[j.joueurId] ?? 0,
                        auteur
                    });
                } catch {
                    failures++;
                }
            }
            if (failures > 0) warning(t('editSeason.adjustFailures', '{n} ajustement(s) n\'ont pas pu être enregistrés', { n: failures }));
            else success(t('editSeason.rankingSaved', 'Classement enregistré'));
            await loadClassement();
        } catch (err) {
            showError(err?.response?.data?.error || t('editSeason.rankingSaveError', 'Erreur lors de l\'enregistrement du classement'));
        } finally {
            setSavingRanking(false);
        }
    };

    const cancelRanking = () => {
        setModifications(Object.fromEntries(classement.map(j => [j.joueurId, j.pointsModifies || 0])));
    };

    const refreshNames = async () => {
        if (classement.length === 0) { info(t('editSeason.noPlayer', 'Aucun joueur dans le classement')); return; }
        const count = await loadNames(classement.map(j => j.joueurId), true);
        if (count === classement.length) success(t('editSeason.namesUpdated', '{n} pseudos mis à jour', { n: count }));
        else warning(t('editSeason.namesPartial', '{n}/{total} pseudos récupérés, réessaie si besoin', { n: count, total: classement.length }));
    };

    const exportCsv = () => {
        if (rows.length === 0) { warning(t('editSeason.nothingToExport', 'Aucun classement à exporter')); return; }
        const headers = [
            t('editSeason.csvRank', 'Rang'), t('editSeason.csvPlayer', 'Joueur'), t('editSeason.csvAttack', 'Pts Attaque'),
            t('editSeason.csvDefense', 'Pts Défense'), t('editSeason.csvTempo', 'Pts Tempo'), t('editSeason.csvBase', 'Points base'),
            t('editSeason.csvAdjust', 'Ajustement'), t('editSeason.csvTotal', 'Total')
        ];
        const lines = rows.map(r => [r.rank, r.name || r.joueurId, r.ptsAttaque || 0, r.ptsDefense || 0, r.tempoPoints || 0, r.points || 0, r.modif, r.total]);
        const csv = '﻿' + [headers, ...lines]
            .map(l => l.map(v => `"${String(v).replace(/"/g, '""')}"`).join(';'))
            .join('\n');
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `classement_${(form.title || 'saison').replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const openPublish = () => {
        if (rankingDirty) {
            warning(t('editSeason.saveRankingFirst', 'Enregistre d\'abord les modifications du classement'));
            return;
        }
        setPublishChannel(form.publicationChannel || '');
        setChannelSearch('');
        setPublishOpen(true);
    };

    const confirmPublish = async () => {
        if (!publishChannel) return;
        setPublishing(true);
        try {
            if (publishChannel !== form.publicationChannel) {
                await api.put(`/saison/${saisonId}`, { ...form, publicationChannel: publishChannel, modifiedBy: auteur });
                setField('publicationChannel', publishChannel);
            }
            await api.post(`/saison/${saisonId}/publish`, { classement, channelId: publishChannel });
            success(t('editSeason.published', 'Classement publié sur Discord'));
            setPublishOpen(false);
        } catch (err) {
            showError(t('editSeason.publishError', 'Échec de la publication : {error}', { error: err?.response?.data?.error || err.message }));
        } finally {
            setPublishing(false);
        }
    };

    if (loading) {
        return (
            <div className="c2-scope c2-admin-page">
                <div className="c2-loading">
                    <div className="c2-spinner" />
                    <p>{t('editSeason.loading', 'Chargement de la saison...')}</p>
                </div>
            </div>
        );
    }
    if (!saison) return null;

    const status = seasonStatus(saison);
    const channelName = (id) => salons.find(s => s.id === id)?.name;
    const filteredSalons = salons.filter(s => s.name.toLowerCase().includes(channelSearch.toLowerCase()));

    return (
        <div className="c2-scope c2-admin-page">
            <Link to={`/server/${serverId}/saison`} className="c2-btn c2-btn--ghost c2-btn--sm season-edit__back">
                <Icon name="arrowLeft" size={16} />
                {t('navigation.seasons', 'Saisons')}
            </Link>

            <PageHeader
                icon="trophy"
                title={saison.title || form.title}
                subtitle={`${fmtDate(saison.startDate)} → ${fmtDate(saison.endDate)}`}
                actions={
                    <>
                        {can(serverId, 'fusion') && (
                            <button
                                type="button"
                                className="c2-btn c2-btn--ghost"
                                onClick={() => navigate(`/server/${serverId}/saisons/merge?base=${saisonId}`)}
                                title={t('editSeason.mergeTitle', 'Fusionner ce classement avec d\'autres saisons')}
                            >
                                <Icon name="copy" size={18} />
                                {t('editSeason.merge', 'Fusionner')}
                            </button>
                        )}
                        <button type="button" className="c2-btn c2-btn--accent" onClick={openPublish} disabled={classement.length === 0}>
                            <Icon name="arrowRight" size={18} />
                            {t('editSeason.publish', 'Publier')}
                        </button>
                    </>
                }
            />

            <div className="season-edit__meta">
                <span className={`c2-chip season-edit__status${status.tone ? ` c2-chip--${status.tone}` : ''}`}>
                    <span className={`c2-diamond${status.tone === 'purple' ? ' c2-diamond--purple' : status.tone ? '' : ' c2-diamond--outline'}`} />
                    {t(`editSeason.status.${status.key}`, status.label)}
                </span>
                <span className="c2-chip"><Icon name="user" size={14} />{classement.length > 1
                    ? t('editSeason.rankedPlayers', '{n} joueurs classés', { n: classement.length })
                    : t('editSeason.rankedPlayer', '{n} joueur classé', { n: classement.length })}</span>
            </div>

            {/* ── Configuration (repliable) ── */}
            <section className={`c2-card c2-section season-config${configOpen ? ' is-open' : ''}`}>
                <button
                    type="button"
                    className="season-config__toggle"
                    onClick={() => setConfigOpen(o => !o)}
                    aria-expanded={configOpen}
                    aria-controls="season-config-body"
                >
                    <span className="c2-section__title"><Icon name="sliders" size={20} />{t('editSeason.configuration', 'Configuration')}</span>
                    <span className="season-config__summary c2-muted">
                        {[
                            attackBaremes.find(b => b._id === form.attackGrade)?.name,
                            defenseBaremes.find(b => b._id === form.defenseGrade)?.name,
                            form.publicationChannel && channelName(form.publicationChannel) && `#${channelName(form.publicationChannel)}`
                        ].filter(Boolean).join(' · ')}
                    </span>
                    <Icon name={configOpen ? 'chevronUp' : 'chevronDown'} size={20} />
                </button>

                {configOpen && (
                    <form id="season-config-body" className="season-config__body" onSubmit={saveConfig}>
                        <div className="season-config__grid">
                            <label className="c2-field">
                                <span className="c2-field__label">{t('createSeasonModal.name', 'Nom de la saison')}</span>
                                <input className="c2-input" value={form.title} onChange={e => setField('title', e.target.value)} required />
                            </label>
                            <label className="c2-field">
                                <span className="c2-field__label">{t('createSeasonModal.publicationChannel', 'Salon de publication')}</span>
                                <select className="c2-select" value={form.publicationChannel} onChange={e => setField('publicationChannel', e.target.value)}>
                                    <option value="">{t('editSeason.none', '— Aucun —')}</option>
                                    {salons.map(c => <option key={c.id} value={c.id}>#{c.name}</option>)}
                                </select>
                            </label>
                            <label className="c2-field">
                                <span className="c2-field__label">{t('createSeasonModal.startDate', 'Date de début')}</span>
                                <input type="date" className="c2-input" value={form.startDate} onChange={e => setField('startDate', e.target.value)} required />
                            </label>
                            <label className="c2-field">
                                <span className="c2-field__label">{t('createSeasonModal.endDate', 'Date de fin')}</span>
                                <input type="date" className="c2-input" value={form.endDate} onChange={e => setField('endDate', e.target.value)} required />
                            </label>
                            <label className="c2-field">
                                <span className="c2-field__label">{t('createSeasonModal.attackBareme', 'Barème attaque')}</span>
                                <select className="c2-select" value={form.attackGrade} onChange={e => setField('attackGrade', e.target.value)} required>
                                    <option value="">{t('createSeasonModal.pickBareme', '— Choisir un barème —')}</option>
                                    {attackBaremes.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
                                </select>
                            </label>
                            <label className="c2-field">
                                <span className="c2-field__label">{t('createSeasonModal.defenseBareme', 'Barème défense')}</span>
                                <select className="c2-select" value={form.defenseGrade} onChange={e => setField('defenseGrade', e.target.value)} required>
                                    <option value="">{t('createSeasonModal.pickBareme', '— Choisir un barème —')}</option>
                                    {defenseBaremes.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
                                </select>
                            </label>
                            {channelConfigs.length > 0 && (
                                <label className="c2-field">
                                    <span className="c2-field__label">{t('createSeasonModal.channelConfig', 'Configuration de salons')}</span>
                                    <select className="c2-select" value={form.channelConfigId} onChange={e => setField('channelConfigId', e.target.value)}>
                                        <option value="">{t('createSeasonModal.defaultConfig', 'Configuration par défaut du serveur')}</option>
                                        {channelConfigs.map(c => <option key={c._id} value={c._id}>{c.name}{c.isDefault ? ` ${t('createSeasonModal.defaultSuffix', '(défaut)')}` : ''}</option>)}
                                    </select>
                                </label>
                            )}
                        </div>

                        <div className="season-config__checks">
                            <label className="season-config__check">
                                <input type="checkbox" className="c2-check" checked={form.autoPublish} onChange={e => setField('autoPublish', e.target.checked)} />
                                {t('createSeasonModal.autoPublish', 'Publier automatiquement le classement en fin de saison')}
                            </label>
                            <label className="season-config__check">
                                <input type="checkbox" className="c2-check" checked={form.includeTempo} onChange={e => setField('includeTempo', e.target.checked)} />
                                {t('createSeasonModal.includeTempo', 'Inclure les points tempo')}
                            </label>
                        </div>

                        <div className="c2-row season-config__actions">
                            <button type="submit" className="c2-btn c2-btn--primary" disabled={savingConfig}>
                                <Icon name="save" size={18} />
                                {savingConfig ? t('editSeason.saving', 'Enregistrement...') : t('editSeason.saveRecalc', 'Enregistrer et recalculer')}
                            </button>
                        </div>
                    </form>
                )}
            </section>

            {/* ── Classement ── */}
            <section className="c2-card c2-section">
                <div className="c2-section__head">
                    <h2 className="c2-section__title"><Icon name="chart" size={20} />{t('editSeason.ranking', 'Classement')}</h2>
                    <div className="season-ranking__tools">
                        <input
                            type="search"
                            className="c2-input c2-input--sm season-ranking__search"
                            placeholder={t('editSeason.searchPlayer', 'Rechercher un joueur...')}
                            aria-label={t('editSeason.searchPlayerLabel', 'Rechercher un joueur')}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                        <button type="button" className="c2-btn c2-btn--ghost c2-btn--sm" onClick={refreshNames} disabled={namesLoading} title={t('editSeason.namesTitle', 'Récupérer à nouveau les pseudos depuis Discord')}>
                            <Icon name="refresh" size={16} />
                            {namesLoading ? t('editSeason.namesLoading', 'Pseudos...') : t('editSeason.names', 'Pseudos')}
                        </button>
                        <button type="button" className="c2-btn c2-btn--ghost c2-btn--sm" onClick={exportCsv} title={t('editSeason.exportTitle', 'Exporter au format CSV (Excel)')}>
                            <Icon name="archive" size={16} />
                            {t('editSeason.export', 'Export CSV')}
                        </button>
                    </div>
                </div>

                {rankingDirty && (
                    <div className="season-ranking__dirty">
                        <span>
                            {changedMods.length > 1
                                ? t('editSeason.unsavedAdjusts', '{n} ajustements non enregistrés', { n: changedMods.length })
                                : t('editSeason.unsavedAdjust', '{n} ajustement non enregistré', { n: changedMods.length })}
                        </span>
                        <div className="c2-row">
                            <button type="button" className="c2-btn c2-btn--ghost c2-btn--sm" onClick={cancelRanking} disabled={savingRanking}>{t('common.cancel', 'Annuler')}</button>
                            <button type="button" className="c2-btn c2-btn--primary c2-btn--sm" onClick={saveRanking} disabled={savingRanking}>
                                <Icon name="save" size={16} />
                                {savingRanking ? t('editSeason.saving', 'Enregistrement...') : t('editSeason.save', 'Enregistrer')}
                            </button>
                        </div>
                    </div>
                )}

                {rankingLoading ? (
                    <div className="c2-loading">
                        <div className="c2-spinner" />
                        <p>{t('editSeason.computing', 'Calcul du classement...')}</p>
                    </div>
                ) : visibleRows.length === 0 ? (
                    <div className="c2-empty">
                        <Icon name="trophy" size={36} />
                        <p>{search ? t('editSeason.noPlayerMatch', 'Aucun joueur ne correspond à la recherche') : t('editSeason.noFight', 'Aucun combat enregistré pour cette saison')}</p>
                    </div>
                ) : (
                    <div className="c2-table-wrap">
                        <table className="c2-table season-ranking">
                            <thead>
                                <tr>
                                    <th className="is-center">#</th>
                                    <th>{t('editSeason.player', 'Joueur')}</th>
                                    <th className="is-num">{t('editSeason.attack', 'Attaque')}</th>
                                    <th className="is-num">{t('editSeason.defense', 'Défense')}</th>
                                    <th className="is-num">{t('editSeason.tempo', 'Tempo')}</th>
                                    <th className="is-num">{t('editSeason.points', 'Points')}</th>
                                    <th className="is-center">{t('editSeason.adjustment', 'Ajustement')}</th>
                                    <th className="is-num">{t('editSeason.total', 'Total')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleRows.map(r => (
                                    <tr key={r.joueurId} className={r.rank <= 3 ? `season-ranking__top season-ranking__top--${r.rank}` : undefined}>
                                        <td className="is-center season-ranking__rank">{r.rank}</td>
                                        <td>
                                            {r.name ? <strong>{r.name}</strong> : (
                                                <span className="c2-muted" title={r.joueurId}>{r.joueurId.slice(0, 8)}…</span>
                                            )}
                                        </td>
                                        <td className="is-num">{r.ptsAttaque ? <span className="season-ranking__att">{fmtNum(r.ptsAttaque)}</span> : <span className="c2-muted">—</span>}</td>
                                        <td className="is-num">{r.ptsDefense ? <span className="season-ranking__def">{fmtNum(r.ptsDefense)}</span> : <span className="c2-muted">—</span>}</td>
                                        <td className="is-num">{r.tempoPoints ? fmtNum(r.tempoPoints) : <span className="c2-muted">—</span>}</td>
                                        <td className="is-num">{fmtNum(r.points)}</td>
                                        <td className="is-center">
                                            <input
                                                className={`c2-input c2-input--sm season-ranking__adjust${r.modif !== (r.pointsModifies || 0) ? ' is-changed' : ''}`}
                                                type="text"
                                                value={adjustDrafts[r.joueurId] ?? (r.modif === 0 ? '' : String(r.modif))}
                                                placeholder="0"
                                                onChange={e => setModification(r.joueurId, e.target.value)}
                                                onFocus={e => e.target.select()}
                                                onBlur={() => endAdjustEdit(r.joueurId)}
                                                aria-label={t('editSeason.adjustFor', 'Ajustement de points pour {name}', { name: r.name || r.joueurId })}
                                            />
                                        </td>
                                        <td className={`is-num season-ranking__total${r.modif > 0 ? ' is-up' : r.modif < 0 ? ' is-down' : ''}`}>
                                            {fmtNum(r.total)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {/* ── Publication ── */}
            {publishOpen && (
                <div className="c2-modal-backdrop" role="presentation">
                    <div className="c2-card c2-modal" role="dialog" aria-modal="true" aria-labelledby="season-publish-title">
                        <div className="c2-modal__head">
                            <h2 id="season-publish-title">{t('editSeason.publishTitle', 'Publier le classement')}</h2>
                            <button type="button" className="c2-icon-btn" onClick={() => setPublishOpen(false)} disabled={publishing} aria-label={t('common.close', 'Fermer')}>
                                <Icon name="close" size={20} />
                            </button>
                        </div>
                        <p className="c2-muted">{t('editSeason.publishDesc', 'Le classement de {n} joueurs sera posté dans le salon choisi.', { n: classement.length })}</p>
                        {salons.length === 0 ? (
                            <p className="season-publish__warning">{t('editSeason.noChannel', 'Aucun salon disponible : vérifie que le bot voit les salons du serveur.')}</p>
                        ) : (
                            <>
                                <input
                                    type="search"
                                    className="c2-input c2-input--sm"
                                    placeholder={t('editSeason.searchChannel', 'Rechercher un salon...')}
                                    aria-label={t('editSeason.searchChannelLabel', 'Rechercher un salon')}
                                    value={channelSearch}
                                    onChange={e => setChannelSearch(e.target.value)}
                                />
                                <ul className="season-publish__channels">
                                    {filteredSalons.map(c => (
                                        <li key={c.id}>
                                            <button
                                                type="button"
                                                className={`season-publish__channel${publishChannel === c.id ? ' is-selected' : ''}`}
                                                onClick={() => setPublishChannel(c.id)}
                                                aria-pressed={publishChannel === c.id}
                                            >
                                                <Icon name="hash" size={16} />
                                                {c.name}
                                                {c.id === form.publicationChannel && <span className="c2-chip c2-chip--green">{t('editSeason.seasonChannel', 'salon de la saison')}</span>}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                        <div className="c2-modal__actions">
                            <button type="button" className="c2-btn c2-btn--ghost" onClick={() => setPublishOpen(false)} disabled={publishing}>{t('common.cancel', 'Annuler')}</button>
                            <button type="button" className="c2-btn c2-btn--accent" onClick={confirmPublish} disabled={!publishChannel || publishing}>
                                {publishing ? t('editSeason.publishing', 'Publication...') : t('editSeason.publish', 'Publier')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EditSaisonPage;
